import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Route as RouteIcon, Loader2, CheckCircle2, AlertCircle, MapPin, Pin, X as IconX } from "lucide-react";
import { toast } from "sonner";
import RutaMapa from "@/components/RutaMapa";

const DEPOT_DEFAULT = {
  address: "Calle Ramón y Cajal 7, 41220 Burguillos, Sevilla",
  lat: 37.5772, lon: -5.9755,
};

const nombreParada = (p) => {
  if (!p) return null;
  if (p.es_manual) return p.manual_titulo || "Parada manual";
  const nombre = p.espacio_nombre || p.espacio?.nombre || p.nombre;
  const ciudad = p.espacio?.ciudad || p.ciudad;
  if (nombre && ciudad && !nombre.toLowerCase().includes(ciudad.toLowerCase())) return `${nombre} · ${ciudad}`;
  return nombre || p.espacio?.direccion || p.direccion || null;
};

export function RouteXLOptimizeDialog({ open, onOpenChange, rutaId, paradas = [], onApplied }) {
  const [origen, setOrigen] = useState({ ...DEPOT_DEFAULT, mode: "depot" });
  const [destino, setDestino] = useState({ mode: "loop", address: "", lat: null, lon: null });
  const [servicetime, setServicetime] = useState(30);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [applying, setApplying] = useState(false);
  const [status, setStatus] = useState(null); // {ok, max_locations, error}
  // Paradas fijas en orden (linea_ids)
  const [pinnedFirst, setPinnedFirst] = useState([]);
  const [pinnedLast, setPinnedLast] = useState([]);

  useEffect(() => {
    if (!open) return;
    setResult(null);
    setPinnedFirst([]);
    setPinnedLast([]);
    // Check status connection RouteXL al abrir
    api.get("/integrations/routexl/status")
      .then((r) => setStatus(r.data))
      .catch((err) => setStatus({ ok: false, error: err?.response?.data?.detail || "Error" }));
  }, [open]);

  const buildPayload = (apply) => ({
    origen_address: origen.address,
    origen_lat: origen.lat,
    origen_lng: origen.lon,
    destino_address: destino.mode === "loop" ? null : destino.address,
    destino_lat: destino.mode === "loop" ? null : destino.lat,
    destino_lng: destino.mode === "loop" ? null : destino.lon,
    servicetime_min: servicetime,
    apply,
    pinned_first_lineas: pinnedFirst,
    pinned_last_lineas: pinnedLast,
  });

  const simular = async () => {
    setLoading(true); setResult(null);
    try {
      const { data } = await api.post(`/rutas/${rutaId}/optimizar-routexl`, buildPayload(false));
      setResult(data);
      if (!data.ok) toast.error(data.error || "RouteXL devolvió un error");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error llamando a RouteXL");
    } finally { setLoading(false); }
  };

  const aplicar = async () => {
    if (!result?.ok) return;
    setApplying(true);
    try {
      const { data } = await api.post(`/rutas/${rutaId}/optimizar-routexl`, buildPayload(true));
      if (data.applied) {
        toast.success("✅ Ruta reordenada");
        onApplied?.();
        onOpenChange(false);
      } else {
        toast.error(data.error || "No se pudo aplicar");
      }
    } catch (err) {
      toast.error("Error aplicando el orden");
    } finally { setApplying(false); }
  };

  // Helpers para gestionar pins
  const togglePin = (lid, slot) => {
    if (slot === "first") {
      setPinnedFirst((arr) => arr.includes(lid) ? arr.filter((x) => x !== lid) : [...arr, lid]);
      setPinnedLast((arr) => arr.filter((x) => x !== lid));
    } else {
      setPinnedLast((arr) => arr.includes(lid) ? arr.filter((x) => x !== lid) : [...arr, lid]);
      setPinnedFirst((arr) => arr.filter((x) => x !== lid));
    }
  };
  const moveInPinned = (lid, slot, dir) => {
    const setter = slot === "first" ? setPinnedFirst : setPinnedLast;
    setter((arr) => {
      const idx = arr.indexOf(lid);
      if (idx < 0) return arr;
      const newIdx = idx + dir;
      if (newIdx < 0 || newIdx >= arr.length) return arr;
      const copy = [...arr];
      [copy[idx], copy[newIdx]] = [copy[newIdx], copy[idx]];
      return copy;
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RouteIcon className="w-5 h-5 text-amber-400" /> Optimizar ruta
          </DialogTitle>
          <div className="text-xs text-zinc-500">Powered by OpenRouteService · 2.500 peticiones/día · 50 paradas máx</div>
        </DialogHeader>

        {/* Status de la integración */}
        {status && !status.ok && (
          <div className="flex items-start gap-2 bg-red-500/10 border border-red-700/40 rounded-lg p-3 text-sm">
            <AlertCircle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold text-red-300">Servicio de optimización no responde</div>
              <div className="text-xs text-zinc-400 mt-0.5">{status.error}</div>
              <div className="text-xs text-zinc-500 mt-1">
                Configura `ORS_API_KEY` en el backend (regístrate gratis en
                <a href="https://openrouteservice.org/dev/#/signup" target="_blank" rel="noreferrer" className="underline text-amber-300 mx-1">openrouteservice.org</a>).
              </div>
            </div>
          </div>
        )}
        {status?.ok && (
          <div className="bg-emerald-500/5 border border-emerald-700/40 rounded-lg p-2 text-xs flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-400" />
            <span className="text-emerald-300">{status.provider || "Conectado"}</span>
            <span className="text-zinc-500">·</span>
            <span className="text-zinc-400">{status.plan}</span>
          </div>
        )}

        {/* Origen / Destino */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-zinc-900/60 border border-zinc-800 rounded-lg p-3">
            <div className="text-xs uppercase tracking-wider text-zinc-400 mb-2 flex items-center gap-1">
              <MapPin className="w-3 h-3" /> Origen
            </div>
            <div className="flex gap-1 mb-2">
              <button onClick={() => setOrigen({ ...DEPOT_DEFAULT, mode: "depot" })}
                className={`px-2 py-1 rounded text-xs ${origen.mode === "depot" ? "bg-amber-500 text-zinc-900 font-semibold" : "bg-zinc-800 text-zinc-300"}`}>
                Nave DNF
              </button>
              <button onClick={() => setOrigen({ mode: "custom", address: "", lat: null, lon: null })}
                className={`px-2 py-1 rounded text-xs ${origen.mode === "custom" ? "bg-amber-500 text-zinc-900 font-semibold" : "bg-zinc-800 text-zinc-300"}`}>
                Personalizado
              </button>
            </div>
            <Input value={origen.address} onChange={(e) => setOrigen({ ...origen, address: e.target.value, mode: "custom" })}
              placeholder="Dirección de salida" className="bg-zinc-950 border-zinc-700 text-sm" data-testid="rxl-origen-addr" />
            <div className="grid grid-cols-2 gap-2 mt-2">
              <Input type="number" step="0.000001" value={origen.lat ?? ""} onChange={(e) => setOrigen({ ...origen, lat: parseFloat(e.target.value) || null })}
                placeholder="Lat" className="bg-zinc-950 border-zinc-700 text-xs font-mono" />
              <Input type="number" step="0.000001" value={origen.lon ?? ""} onChange={(e) => setOrigen({ ...origen, lon: parseFloat(e.target.value) || null })}
                placeholder="Lon" className="bg-zinc-950 border-zinc-700 text-xs font-mono" />
            </div>
          </div>

          <div className="bg-zinc-900/60 border border-zinc-800 rounded-lg p-3">
            <div className="text-xs uppercase tracking-wider text-zinc-400 mb-2 flex items-center gap-1">
              <MapPin className="w-3 h-3" /> Destino final
            </div>
            <div className="flex gap-1 mb-2">
              <button onClick={() => setDestino({ mode: "loop", address: "", lat: null, lon: null })}
                className={`px-2 py-1 rounded text-xs ${destino.mode === "loop" ? "bg-amber-500 text-zinc-900 font-semibold" : "bg-zinc-800 text-zinc-300"}`}>
                Volver al origen (loop)
              </button>
              <button onClick={() => setDestino({ mode: "custom", address: "", lat: null, lon: null })}
                className={`px-2 py-1 rounded text-xs ${destino.mode === "custom" ? "bg-amber-500 text-zinc-900 font-semibold" : "bg-zinc-800 text-zinc-300"}`}>
                Otro punto
              </button>
            </div>
            {destino.mode === "custom" && (
              <>
                <Input value={destino.address} onChange={(e) => setDestino({ ...destino, address: e.target.value })}
                  placeholder="Dirección final" className="bg-zinc-950 border-zinc-700 text-sm" />
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <Input type="number" step="0.000001" value={destino.lat ?? ""} onChange={(e) => setDestino({ ...destino, lat: parseFloat(e.target.value) || null })}
                    placeholder="Lat" className="bg-zinc-950 border-zinc-700 text-xs font-mono" />
                  <Input type="number" step="0.000001" value={destino.lon ?? ""} onChange={(e) => setDestino({ ...destino, lon: parseFloat(e.target.value) || null })}
                    placeholder="Lon" className="bg-zinc-950 border-zinc-700 text-xs font-mono" />
                </div>
              </>
            )}
            {destino.mode === "loop" && (
              <div className="text-xs text-zinc-500 italic">La ruta termina volviendo a la nave (loop).</div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Label className="text-xs">Tiempo de montaje por parada (min):</Label>
          <Input type="number" min="0" max="600" value={servicetime}
            onChange={(e) => setServicetime(parseInt(e.target.value) || 0)}
            className="bg-zinc-900 border-zinc-700 w-20 text-sm" />
        </div>

        {/* 📌 Paradas fijas (cita / obligación) */}
        {paradas && paradas.length > 0 && (
          <div className="bg-zinc-900/40 border border-zinc-800 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-2">
              <Pin className="w-4 h-4 text-amber-400" />
              <span className="text-xs font-semibold text-zinc-200">Paradas con orden obligado</span>
              <span className="text-[10px] text-zinc-500">(p.ej. apertura tienda, cita)</span>
            </div>
            {(pinnedFirst.length === 0 && pinnedLast.length === 0) ? (
              <div className="text-[11px] text-zinc-500 italic mb-2">Ninguna fijada. El optimizador decidirá libremente.</div>
            ) : (
              <div className="space-y-1.5 mb-2">
                {pinnedFirst.map((lid, i) => {
                  const p = paradas.find((x) => x.linea_id === lid);
                  return (
                    <div key={lid} data-testid={`pin-first-${lid}`}
                      className="flex items-center gap-2 bg-emerald-500/10 border border-emerald-700/40 rounded px-2 py-1 text-xs">
                      <span className="font-mono text-emerald-400">⬆ #{i + 1}</span>
                      <span className="flex-1 truncate">{nombreParada(p) || lid.slice(0, 8)}</span>
                      <button onClick={() => moveInPinned(lid, "first", -1)} className="text-zinc-400 hover:text-zinc-100 px-1" title="Subir">↑</button>
                      <button onClick={() => moveInPinned(lid, "first", +1)} className="text-zinc-400 hover:text-zinc-100 px-1" title="Bajar">↓</button>
                      <button onClick={() => togglePin(lid, "first")} className="text-red-400 hover:text-red-300" title="Quitar">
                        <IconX className="w-3 h-3" />
                      </button>
                    </div>
                  );
                })}
                {pinnedLast.map((lid, i) => {
                  const p = paradas.find((x) => x.linea_id === lid);
                  return (
                    <div key={lid} data-testid={`pin-last-${lid}`}
                      className="flex items-center gap-2 bg-violet-500/10 border border-violet-700/40 rounded px-2 py-1 text-xs">
                      <span className="font-mono text-violet-300">⬇ último #{i + 1}</span>
                      <span className="flex-1 truncate">{nombreParada(p) || lid.slice(0, 8)}</span>
                      <button onClick={() => moveInPinned(lid, "last", -1)} className="text-zinc-400 hover:text-zinc-100 px-1" title="Subir">↑</button>
                      <button onClick={() => moveInPinned(lid, "last", +1)} className="text-zinc-400 hover:text-zinc-100 px-1" title="Bajar">↓</button>
                      <button onClick={() => togglePin(lid, "last")} className="text-red-400 hover:text-red-300" title="Quitar">
                        <IconX className="w-3 h-3" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
            <details>
              <summary className="text-[11px] text-amber-300 cursor-pointer hover:text-amber-200">＋ Añadir parada fija…</summary>
              <div className="mt-2 grid gap-1 max-h-44 overflow-auto pr-1">
                {paradas
                  .filter((p) => p.linea_id && !p.es_manual && !pinnedFirst.includes(p.linea_id) && !pinnedLast.includes(p.linea_id))
                  .map((p) => (
                    <div key={p.linea_id} className="flex items-center gap-2 bg-zinc-950 border border-zinc-800 rounded px-2 py-1 text-xs">
                      <span className="flex-1 truncate">{nombreParada(p) || `Parada #${p.orden ?? ""}`}</span>
                      <button onClick={() => togglePin(p.linea_id, "first")}
                        data-testid={`add-pin-first-${p.linea_id}`}
                        className="px-2 py-0.5 rounded text-[10px] bg-emerald-500/15 border border-emerald-700/40 text-emerald-300 hover:bg-emerald-500/25">
                        Primero
                      </button>
                      <button onClick={() => togglePin(p.linea_id, "last")}
                        data-testid={`add-pin-last-${p.linea_id}`}
                        className="px-2 py-0.5 rounded text-[10px] bg-violet-500/15 border border-violet-700/40 text-violet-300 hover:bg-violet-500/25">
                        Última
                      </button>
                    </div>
                  ))}
              </div>
            </details>
          </div>
        )}

        {/* Resultado */}
        {result?.ok && (
          <div className="bg-emerald-500/5 border border-emerald-700/40 rounded-lg p-3">
            <div className="grid grid-cols-2 gap-3 mb-3 text-center">
              <div>
                <div className="text-[10px] uppercase text-zinc-500">Duración total</div>
                <div className="text-2xl font-bold text-emerald-400">
                  {result.total_duration_min != null
                    ? `${Math.floor(result.total_duration_min / 60)}h ${result.total_duration_min % 60}min`
                    : "—"}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase text-zinc-500">Paradas</div>
                <div className="text-2xl font-bold">{result.new_order?.length || 0}</div>
              </div>
            </div>
            <div className="text-xs text-zinc-400 mb-2 font-semibold">Orden óptimo propuesto:</div>
            <div className="max-h-48 overflow-auto bg-zinc-950/50 rounded p-2 space-y-1">
              {result.new_order.map((p, i) => (
                <div key={i} className="flex items-center gap-2 text-xs">
                  <span className="font-mono text-amber-400 w-6">#{i + 1}</span>
                  {p.pinned === "first" && <span className="text-[9px] uppercase px-1 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/40">📌 Fijo</span>}
                  {p.pinned === "last" && <span className="text-[9px] uppercase px-1 rounded bg-violet-500/20 text-violet-300 border border-violet-500/40">📌 Final</span>}
                  <span className="flex-1 truncate">{p.espacio_nombre}</span>
                  {p.arrival_min != null && (
                    <span className="text-zinc-500 font-mono">
                      llegada {Math.floor(p.arrival_min/60)}h{(p.arrival_min%60).toString().padStart(2,'0')}
                    </span>
                  )}
                </div>
              ))}
            </div>
            {/* 🗺️ Mapa con el orden propuesto */}
            <div className="mt-3" data-testid="optimize-mapa-preview">
              <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5">Vista previa del recorrido</div>
              <RutaMapa
                rutaId={rutaId}
                orden={result.new_order.map((p) => p.linea_id)}
                height={240}
              />
            </div>
          </div>
        )}
        {result && !result.ok && (
          <div className="bg-red-500/10 border border-red-700/40 rounded-lg p-3 text-sm text-red-300">
            ❌ {result.error}
          </div>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}
                  className="bg-transparent border-zinc-700">Cancelar</Button>
          <Button onClick={simular} disabled={loading || !status?.ok}
                  variant="outline" className="bg-transparent border-amber-700/40 text-amber-300">
            {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Calculando…</> : "Simular orden óptimo"}
          </Button>
          <Button onClick={aplicar} disabled={applying || !result?.ok}
                  className="bg-amber-500 hover:bg-amber-400 text-zinc-900 font-semibold">
            {applying ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Aplicando…</> : "Aplicar a la ruta"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
